import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Un colore per agente, assegnato in ordine alfabetico (stabile tra i caricamenti)
const AGENT_COLORS = ["#f59e0b", "#38bdf8", "#4ade80", "#f472b6", "#a78bfa", "#fb7185", "#2dd4bf", "#facc15", "#fb923c", "#94a3b8"];
const buildAgentColors = (items) => {
  const names = [...new Set(items.map((o) => o.created_by_name || "Sconosciuto"))].sort((a, b) => a.localeCompare(b));
  return Object.fromEntries(names.map((n, i) => [n, AGENT_COLORS[i % AGENT_COLORS.length]]));
};
const agentOf = (o) => o.created_by_name || "Sconosciuto";

const fmtDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);

const DistanceBadge = ({ o }) => {
  if (o.dest_geocode_status === "pending") {
    return <span className="text-[10px] text-zinc-500 shrink-0">calcolo…</span>;
  }
  if (o.dest_geocode_status === "no_address") {
    return <span className="text-[10px] text-zinc-500 shrink-0">destinazione senza indirizzo</span>;
  }
  if (o.dest_distance_m == null) {
    return <span className="text-[10px] text-zinc-500 shrink-0">indirizzo non trovato</span>;
  }
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${
        o.dest_onsite
          ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
          : "bg-red-500/10 border-red-500/40 text-red-300"
      }`}
    >
      {o.dest_onsite ? "In loco" : `Fuori sede · ${fmtDist(o.dest_distance_m)}`}
    </span>
  );
};

// Pin numerato: gli ordini presi nello stesso punto (~11 m) diventano un solo marker.
// Più agenti nello stesso punto = pin a spicchi con i loro colori.
const pinIcon = (colors, count) => {
  const bg = colors.length > 1
    ? `conic-gradient(${colors.map((c, i) => `${c} ${(i * 100) / colors.length}% ${((i + 1) * 100) / colors.length}%`).join(",")})`
    : colors[0];
  return L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50%;background:${bg};border:2px solid #0f0f11;
      display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.5)">
      ${count > 1 ? `<span style="background:#0f0f11;color:#fff;border-radius:999px;min-width:18px;height:18px;
        display:flex;align-items:center;justify-content:center;font:700 11px/1 ui-monospace,monospace">${count}</span>` : ""}
      </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -14],
  });
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 16 });
  }, [points, map]);
  return null;
};

const groupByPoint = (items) => {
  const map = new Map();
  items.forEach((o) => {
    const key = `${o.geo_lat.toFixed(4)},${o.geo_lng.toFixed(4)}`;
    if (!map.has(key)) map.set(key, { key, lat: o.geo_lat, lng: o.geo_lng, orders: [] });
    map.get(key).orders.push(o);
  });
  return [...map.values()];
};

export default function AdminGiriMap() {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [giri, setGiri] = useState([]);
  const [filters, setFilters] = useState({ date_from: todayISO(), date_to: todayISO(), giro: "" });
  const [agentFilter, setAgentFilter] = useState("");
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
      const r = await authAxios.get(`/orders-geo?${params}`);
      setItems(r.data.items || []);
      setGiri(r.data.giri || []);
      setPending(r.data.geocoding_pending || 0);
    } catch {
      toast.error("Errore caricamento posizioni");
    } finally { setLoading(false); }
  }, [authAxios, filters]);

  useEffect(() => { load(); }, [load]);

  // Gli indirizzi non ancora geocodificati si risolvono in background (1/s): ricarico finché restano
  useEffect(() => {
    if (!pending) return undefined;
    const t = setTimeout(load, Math.min(3000 + pending * 1200, 30000));
    return () => clearTimeout(t);
  }, [pending, load]);

  const agentColors = useMemo(() => buildAgentColors(items), [items]);
  const visible = useMemo(
    () => (agentFilter ? items.filter((o) => agentOf(o) === agentFilter) : items),
    [items, agentFilter]
  );
  const groups = useMemo(() => groupByPoint(visible), [visible]);

  const center = items.length
    ? [items[0].geo_lat, items[0].geo_lng]
    : [38.1157, 15.0]; // Sicilia nord-orientale

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="giri-map-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto pb-16">
        <PageHeader title="Mappa presa ordini" />

        <Card className="bg-[#18181b] border-white/10 mb-4">
          <CardContent className="p-4">
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Da</label>
                <Input
                  type="date"
                  data-testid="geo-from"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  className="h-9 w-36 bg-[#0f0f11] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">A</label>
                <Input
                  type="date"
                  data-testid="geo-to"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  className="h-9 w-36 bg-[#0f0f11] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Giro</label>
                <select
                  data-testid="geo-giro"
                  value={filters.giro}
                  onChange={(e) => setFilters({ ...filters, giro: e.target.value })}
                  className="h-9 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm px-2"
                >
                  <option value="">Tutti</option>
                  {giri.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Agente</label>
                <select
                  data-testid="geo-agent"
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  className="h-9 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm px-2 max-w-[180px]"
                >
                  <option value="">Tutti</option>
                  {Object.keys(agentColors).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <Button onClick={load} disabled={loading} data-testid="geo-refresh" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
              </Button>
              <span className="text-xs text-zinc-500 ml-auto" data-testid="geo-count">
                {visible.length} ordini con posizione
                {pending > 0 ? ` · ${pending} indirizzi in calcolo` : ""}
              </span>
            </div>
          </CardContent>
        </Card>

        <div className="rounded-xl overflow-hidden border border-white/10 mb-4" style={{ height: "60vh" }}>
          <MapContainer center={center} zoom={items.length ? 11 : 8} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={groups} />
            {groups.map((g) => (
              <Marker
                key={g.key}
                position={[g.lat, g.lng]}
                icon={pinIcon([...new Set(g.orders.map((o) => agentColors[agentOf(o)]))], g.orders.length)}
              >
                <Popup minWidth={220} maxHeight={240}>
                  {g.orders.length > 1 && (
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{g.orders.length} ordini in questo punto</div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {g.orders.map((o) => (
                      <div key={o.id} style={{ borderTop: g.orders.length > 1 ? "1px solid #e5e7eb" : "none", paddingTop: 6 }}>
                        <strong>{o.numero}</strong> · € {(o.total || 0).toFixed(2)}<br />
                        {o.client_name}{o.destinazione_nome ? ` → ${o.destinazione_nome}` : ""}<br />
                        {o.giro_provenienza ? `Giro ${o.giro_provenienza} · ` : ""}
                        {new Date(o.created_at).toLocaleString("it-IT")}<br />
                        <span
                          style={{
                            display: "inline-block", width: 9, height: 9, borderRadius: "50%",
                            background: agentColors[agentOf(o)], marginRight: 5,
                          }}
                        />
                        {o.created_by_name || ""}{o.geo_accuracy ? ` · ±${Math.round(o.geo_accuracy)} m` : ""}
                        {o.dest_distance_m != null && (
                          <>
                            <br />
                            <span style={{ color: o.dest_onsite ? "#047857" : "#b91c1c", fontWeight: 600 }}>
                              {o.dest_onsite ? "In loco" : `Fuori sede · ${fmtDist(o.dest_distance_m)}`}
                            </span>
                            {" dalla destinazione"}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {items.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4" data-testid="geo-legend">
            {Object.entries(agentColors).map(([name, color]) => (
              <button
                key={name}
                type="button"
                onClick={() => setAgentFilter(agentFilter === name ? "" : name)}
                className={`flex items-center gap-2 text-xs transition-opacity ${
                  agentFilter && agentFilter !== name ? "opacity-35 hover:opacity-70" : "text-zinc-200"
                }`}
                data-testid={`geo-legend-${name}`}
              >
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
                {name}
                <span className="text-zinc-500 font-mono">
                  {items.filter((o) => agentOf(o) === name).length}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="space-y-2" data-testid="geo-list">
          {visible.map((o) => (
            <a
              key={o.id}
              href={`https://www.google.com/maps?q=${o.geo_lat},${o.geo_lng}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`geo-item-${o.id}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-[#18181b] border border-white/10 hover:border-amber-500/40 transition-colors"
            >
              <MapPin className="w-4 h-4 shrink-0" style={{ color: agentColors[agentOf(o)] }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                  {o.numero} · {o.client_name}{o.destinazione_nome ? ` → ${o.destinazione_nome}` : ""}
                </p>
                <p className="text-[11px] text-zinc-500 truncate">
                  {o.giro_provenienza ? `Giro ${o.giro_provenienza} · ` : ""}
                  {new Date(o.created_at).toLocaleString("it-IT")}
                  {o.created_by_name ? ` · ${o.created_by_name}` : ""}
                </p>
              </div>
              <span data-testid={`geo-dist-${o.id}`}><DistanceBadge o={o} /></span>
              <span className="text-xs text-zinc-400 font-mono shrink-0">€ {(o.total || 0).toFixed(2)}</span>
            </a>
          ))}
          {!visible.length && !loading && (
            <p className="text-zinc-500 text-sm">Nessun ordine con posizione nel periodo selezionato.</p>
          )}
        </div>
      </main>
    </div>
  );
}
